import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { formatDateUK, formatDateForInput, parseDateFromInput } from '../lib/dateUtils'

export function SettingsPage() {
  const [acceptOrders, setAcceptOrders] = useState(true)
  const [eventDate, setEventDate] = useState<string>('')
  const [dateDraft, setDateDraft] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingDate, setSavingDate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  async function loadSettings() {
    try {
      setLoading(true)
      setError(null)
      const settings = await api.getAdminSettings()
      const acceptOrdersSetting = settings.find(s => s.key === 'acceptOrders')
      setAcceptOrders(acceptOrdersSetting?.value === 'true')

      const eventDateSetting = settings.find(s => s.key === 'eventDate')
      if (eventDateSetting?.value) {
        setEventDate(eventDateSetting.value)
        setDateDraft(formatDateForInput(eventDateSetting.value))
      }
    } catch (err) {
      setError('Failed to load settings')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function handleToggle() {
    const newValue = !acceptOrders

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      await api.updateAdminSetting('acceptOrders', newValue ? 'true' : 'false')

      setAcceptOrders(newValue)
      setSuccessMessage(newValue ? 'Orders are now being accepted' : 'Orders have been turned off')

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update setting')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  // Only a complete YYYY-MM-DD that differs from the saved date can be saved; never empty.
  const isoDraft = parseDateFromInput(dateDraft)
  const canSaveDate = /^\d{4}-\d{2}-\d{2}$/.test(isoDraft) && !isNaN(new Date(isoDraft + 'T00:00:00').getTime()) && isoDraft !== formatDateForInput(eventDate)

  async function saveEventDate() {
    if (!canSaveDate) return
    const isoDate = isoDraft

    try {
      setSavingDate(true)
      setError(null)
      setSuccessMessage(null)

      await api.updateAdminSetting('eventDate', isoDate)

      setEventDate(isoDate)
      setSuccessMessage(`Event date updated to ${formatDateUK(isoDate)}`)

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update event date')
      console.error(err)
    } finally {
      setSavingDate(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Settings</h1>

      {error && (
        <div role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div role="status" className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 id="accept-orders-label" className="text-lg font-semibold text-gray-800">Accept Orders</h2>
            <p className="text-sm text-gray-600 mt-1">
              {acceptOrders
                ? 'The workshop is currently active and accepting orders'
                : 'The workshop is currently disabled - visitors will see "Santa is sleeping. See you next year!"'}
            </p>
          </div>

          <button
            onClick={handleToggle}
            disabled={saving}
            role="switch"
            aria-checked={acceptOrders}
            aria-labelledby="accept-orders-label"
            className={`
              relative inline-flex h-8 w-14 items-center rounded-full transition-colors
              ${acceptOrders ? 'bg-green-500' : 'bg-gray-300'}
              ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <span
              className={`
                inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                ${acceptOrders ? 'translate-x-7' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <p className="mb-2">
              <strong>Status:</strong> {acceptOrders ? (
                <span className="text-green-700 font-semibold">Accepting Orders</span>
              ) : (
                <span className="text-red-600 font-semibold">Orders Disabled</span>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Toggle this setting to control whether the Elves' Workshop wizard is available to visitors.
              When disabled, visitors will see a message that Santa is sleeping instead of the order form.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Event Date</h2>
          <p className="text-sm text-gray-600 mt-1">
            Set the date of the Christmas Fayre. This date is used throughout the app for emails and user information.
          </p>
        </div>

        <div className="mt-4">
          <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-2">
            Christmas Fayre Date
          </label>
          <div className="flex items-center gap-4">
            <input
              type="date"
              id="eventDate"
              value={dateDraft}
              onChange={(e) => setDateDraft(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <button
              onClick={saveEventDate}
              disabled={!canSaveDate || savingDate}
              className="min-h-[44px] px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingDate ? 'Saving...' : 'Save Date'}
            </button>
          </div>
          {eventDate && (
            <p className="mt-2 text-sm text-gray-600">
              Current date: <strong>{formatDateUK(eventDate)}</strong>
            </p>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <p className="text-xs text-gray-500">
              This date will be displayed in confirmation emails and on the order form.
              The format will always be shown in UK format (e.g., Friday 12th December).
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
